/**
 * Telling the server the two things it cannot see from answers
 * ([Telemetry](../../../docs/TELEMETRY.md)).
 *
 * **Nothing here is awaited and nothing here can fail visibly.** Telemetry is
 * the least important thing this app does: a dropped event is a worse dashboard,
 * and an event that throws is a worse app. Every call returns immediately and
 * swallows whatever happens next — the same shape as filing a report
 * ([D-073](../../../docs/DECISIONS.md#d-073)).
 *
 * The client is held in a module variable rather than threaded through every
 * component, because the one place that most needs to report an error is
 * [`Boundary`](./Boundary.tsx) — a class component that sits above the tree and
 * deliberately knows nothing about the app inside it. Passing a client into
 * every boundary would put a prop on every caller for the benefit of the rare
 * one that breaks.
 */

import { scrub } from '@capytalk/core';
import { appVersion } from './version.js';
import type { Client } from './api.js';

let reporter: Client | null = null;

/** Called once, by the app, as soon as there is a client to report through. */
export function reportTo(client: Client | null): void {
  reporter = client;
}

/**
 * Send, and never let it matter whether it arrived.
 *
 * Through `Promise.resolve` because a server older than this app has no
 * `telemetry` router at all, and the call *throws* rather than rejecting
 * ([D-066](../../../docs/DECISIONS.md#d-066)) — which from a React error
 * boundary would mean an error while handling an error.
 */
function send(what: (client: Client) => Promise<unknown>): void {
  const client = reporter;
  if (client === null) return;
  Promise.resolve()
    .then(() => what(client))
    .catch(() => {
      // Swallowed on purpose. See the note at the top of this file.
    });
}

/**
 * Something broke.
 *
 * Scrubbed **here as well as on the server**: the rule lives in `packages/core`
 * so that neither side is the only one applying it, and the sooner a child's own
 * words are gone the fewer places they have been.
 */
export function reportError(input: {
  readonly code: string;
  readonly screen: string;
  readonly message: string;
}): void {
  send((client) =>
    client.telemetry.error.mutate({
      code: input.code.slice(0, 64),
      screen: input.screen.slice(0, 64),
      message: scrub(input.message),
      // The content version is not sent: the server knows which course this
      // learner is on and what it was seeded at, and a stale client would get
      // it wrong exactly when triage needs it right.
      appVersion: appVersion(),
    }),
  );
}

/** Where a child gave up. */
export function reportAbandon(input: {
  readonly lessonId: string;
  readonly sessionId: string;
  readonly exerciseOrdinal: number;
  readonly elapsedMs: number;
}): void {
  send((client) =>
    client.telemetry.abandoned.mutate({
      ...input,
      // The only reason the web can actually tell apart. `backgrounded` and
      // `session_end` need lifecycle signals a browser tab does not reliably
      // give, and guessing between them would put a wrong value in a column
      // whose whole purpose is to be read later.
      reason: 'closed',
    }),
  );
}
